import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Drawer, Box, List, ListItemButton, ListItemIcon,
  ListItemText, Chip, Avatar, Menu, MenuItem, IconButton, Divider, Tooltip
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupIcon from '@mui/icons-material/Group';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import KeyIcon from '@mui/icons-material/Key';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../context/AuthContext';
import OrgSwitcher from '../components/OrgSwitcher';

const DRAWER_WIDTH = 248;

const NAV = [
  { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { label: 'Entitlements', path: '/entitlements', icon: <VerifiedUserIcon /> },
  { label: 'Users', path: '/users', icon: <GroupIcon /> },
  { label: 'Roles', path: '/roles', icon: <AdminPanelSettingsIcon /> },
  { label: 'Permissions', path: '/permissions', icon: <KeyIcon /> },
  { label: 'User Roles', path: '/user-roles', icon: <ManageAccountsIcon /> },
  { label: 'M2M Test', path: '/m2m', icon: <VpnKeyIcon /> },
  { label: 'Key Rotation', path: '/key-rotation', icon: <AutorenewIcon /> },
  { label: 'Permission Sync', path: '/permission-sync', icon: <SyncAltIcon /> },
  { label: 'Token Inspector', path: '/token-inspector', icon: <TravelExploreIcon /> }
];

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, productId, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  const initials = (user?.name || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Toolbar>
          <Box component="img" src="/logo.png" alt="Milient" sx={{ height: 28, mr: 1.5 }} />
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            SSO Simulator
          </Typography>
          {productId && (
            <Chip label={productId} size="small" color="primary" variant="outlined" sx={{ ml: 1.5, fontFamily: 'monospace' }} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <OrgSwitcher />
          <Box sx={{ width: 8 }} />
          <Tooltip title={user?.email || ''}>
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} size="small">
              <Avatar sx={{ bgcolor: 'primary.main', width: 34, height: 34, fontSize: 14 }}>{initials}</Avatar>
            </IconButton>
          </Tooltip>
          <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={() => setAnchorEl(null)}>
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Box>
                <Typography variant="subtitle2">{user?.name || 'Signed in'}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.email}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={logout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' }
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 1 }}>
          <List>
            {NAV.map((item) => {
              const selected = location.pathname === item.path;
              return (
                <ListItemButton
                  key={item.path}
                  selected={selected}
                  onClick={() => navigate(item.path)}
                  sx={{
                    mx: 1, my: 0.25, borderRadius: 2,
                    '&.Mui-selected': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                    '&.Mui-selected:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-selected .MuiListItemIcon-root': { color: 'primary.contrastText' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 600, fontSize: 14.5 }} />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, width: `calc(100% - ${DRAWER_WIDTH}px)` }}>
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default AppLayout;
